/**
 * .tuMetrics {"tu1": 0.94, "tu2": 0.95, "tu3": 3, "notes": "Pure follow type + event matching + parent refs for fan-out", "tuVersion": "2.5"}
 * .help "Which objects you can Follow, and whether a feed event is about something you follow."
 */
import type { Card, FeedEvent } from '../../types';

export const FOLLOW_ENTITY_TYPES = [
  'task',
  'idea',
  'bug',
  'sprint',
  'roadmap',
  'requirement',
  'project',
  'plan',
] as const;

export type FollowEntityType = (typeof FOLLOW_ENTITY_TYPES)[number];

export function isFollowEntityType(value: string): value is FollowEntityType {
  return (FOLLOW_ENTITY_TYPES as readonly string[]).includes(value);
}

export type FollowRef = { entityType: string; entityId: string; userId?: string };

function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && String(a).trim() === String(b).trim();
}

/** Comment / follow rows use 'requirement' for features and task|idea (not 'card') for cards. */
export function followMatchesEntity(
  follow: FollowRef,
  entityType: string,
  entityId: string,
): boolean {
  if (!sameId(follow.entityId, entityId)) return false;
  if (follow.entityType === entityType) return true;
  if (entityType === 'card' && (follow.entityType === 'task' || follow.entityType === 'idea' || follow.entityType === 'bug')) return true;
  if (entityType === 'feature' && follow.entityType === 'requirement') return true;
  return false;
}

export function followRefsFromCard(card: {
  id: string;
  type?: string | null;
  projectId?: string | null;
  sprintId?: string | null;
  roadmapId?: string | null;
}): FollowRef[] {
  const refs: FollowRef[] = [];
  if (card.type === 'task' || card.type === 'bug' || card.type === 'idea') {
    refs.push({ entityType: card.type, entityId: card.id });
  }
  if (card.projectId) refs.push({ entityType: 'project', entityId: card.projectId });
  if (card.sprintId) refs.push({ entityType: 'sprint', entityId: card.sprintId });
  if (card.roadmapId) refs.push({ entityType: 'roadmap', entityId: card.roadmapId });
  return refs;
}

export function followRefsFromRequirement(req: {
  id: string;
  projectId?: string | null;
  roadmapId?: string | null;
}): FollowRef[] {
  const refs: FollowRef[] = [{ entityType: 'requirement', entityId: req.id }];
  if (req.projectId) refs.push({ entityType: 'project', entityId: req.projectId });
  if (req.roadmapId) refs.push({ entityType: 'roadmap', entityId: req.roadmapId });
  return refs;
}

export function followRefsFromSprint(sprint: {
  id: string;
  projectId?: string | null;
  roadmapId?: string | null;
}): FollowRef[] {
  const refs: FollowRef[] = [{ entityType: 'sprint', entityId: sprint.id }];
  if (sprint.projectId) refs.push({ entityType: 'project', entityId: sprint.projectId });
  if (sprint.roadmapId) refs.push({ entityType: 'roadmap', entityId: sprint.roadmapId });
  return refs;
}

export function uniqueFollowRefs(refs: FollowRef[]): FollowRef[] {
  const seen = new Set<string>();
  const out: FollowRef[] = [];
  for (const ref of refs) {
    if (!ref.entityId || !isFollowEntityType(ref.entityType)) continue;
    const key = `${ref.entityType}:${String(ref.entityId).trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ entityType: ref.entityType, entityId: String(ref.entityId).trim() });
  }
  return out;
}

export function followerUserIdsMatchingRefs(
  rows: Array<{ userId: string; entityType: string; entityId: string }>,
  refs: FollowRef[],
  skipUserIds: Array<string | null | undefined> = [],
): string[] {
  const skip = new Set(
    skipUserIds.filter((id): id is string => typeof id === 'string' && id.length > 0),
  );
  const cleaned = uniqueFollowRefs(refs);
  const recipients = new Set<string>();
  for (const row of rows) {
    if (!row.userId || skip.has(row.userId)) continue;
    if (cleaned.some((r) => r.entityType === row.entityType && sameId(r.entityId, row.entityId))) {
      recipients.add(row.userId);
    }
  }
  return [...recipients];
}

export function eventMatchesFollows(
  event: Pick<FeedEvent, 'cardId' | 'sprintId' | 'roadmapId' | 'requirementId' | 'projectId' | 'planId'>,
  follows: FollowRef[],
  cards: Pick<Card, 'id' | 'type' | 'sprintId' | 'roadmapId' | 'projectId'>[] = [],
): boolean {
  if (follows.length === 0) return false;
  const hit = (entityType: FollowEntityType, entityId: string | null | undefined) =>
    !!entityId && follows.some((f) => f.entityType === entityType && sameId(f.entityId, entityId));

  if (hit('sprint', event.sprintId ?? null)) return true;
  if (hit('roadmap', event.roadmapId ?? null)) return true;
  if (hit('requirement', event.requirementId ?? null)) return true;
  if (hit('project', event.projectId ?? null)) return true;
  if (hit('plan', event.planId ?? null)) return true;

  if (event.cardId) {
    const card = cards.find((c) => sameId(c.id, event.cardId));
    if (card) {
      if (card.type === 'task' && hit('task', card.id)) return true;
      if (card.type === 'bug' && hit('bug', card.id)) return true;
      if (card.type === 'idea' && hit('idea', card.id)) return true;
      if (hit('sprint', card.sprintId ?? null)) return true;
      if (hit('roadmap', card.roadmapId ?? null)) return true;
      if (hit('project', card.projectId ?? null)) return true;
    } else if (hit('task', event.cardId) || hit('bug', event.cardId) || hit('idea', event.cardId)) {
      return true;
    }
  }
  return false;
}
